import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  styled,
  Link as MuiLink,
  CircularProgress,
  Alert,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "../../../assets/images/logo.png";
import { useAuth } from "../../../contexts/AuthContext";
import SnackbarMessage from "../../../components/common/SnackbarMessage";
import { useTranslation } from "react-i18next";
// Import visibility icons
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const AuthWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  backgroundColor: "#fff",
  overflow: "hidden",
}));

const AuthCard = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 450,
  margin: "auto",
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: -200,
    right: -200,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    zIndex: 0,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -200,
    left: -200,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    zIndex: 0,
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const ContentBox = styled(Box)({
  width: "100%",
  position: "relative",
  zIndex: 1,
  backgroundColor: "#fff",
  borderRadius: 24,
  padding: "32px 24px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
});

const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 24,
  "& img": {
    height: 40,
  },
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: 16,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: "#F5F5F5",
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const BackToHomeLink = styled(Link)(({ theme }) => ({
  position: "absolute",
  top: 20,
  left: 20,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textDecoration: "none",
  fontSize: "0.9rem",
  padding: "8px 16px",
  borderRadius: 20,
  transition: "all 0.2s ease",
  zIndex: 2,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.text.primary,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",

  "&:hover": {
    backgroundColor: "#fff",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const ArrowIcon = styled("span")({
  display: "inline-block",
  marginRight: "4px",
  transition: "transform 0.2s ease",
  "& svg": {
    fontSize: "1.2rem",
  },
});

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  const { login, loading, error, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    title: "",
  });

  // Get the redirect path and message from location state
  const from = location.state?.from || "/";
  const message = location.state?.message;
  const search = location.state?.search || "";
  const previousState = location.state?.previousState;

  // Add password visibility state
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      setSnackbar({
        open: true,
        message: t('auth.login.messages.success'),
        severity: "success",
        title: "Success",
      });

      // Reconstruct the full URL with search parameters
      const redirectPath = `${from}${search}`;

      // Small delay before navigation for better UX
      setTimeout(() => {
        navigate(redirectPath, {
          state: previousState,
          replace: true,
        });
      }, 1000);
    }
  }, [isAuthenticated, navigate, from, search, previousState, t]);

  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: "error",
        title: "Error",
      });
    }
  }, [error]);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }
    if (!formData.password) {
      errors.password = "Password is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Add toggle handler
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await login(formData);
        // The redirection will be handled by the useEffect above
      } catch (err) {
        // Error is handled by the context and shown via snackbar
      }
    } else {
      setSnackbar({
        open: true,
        message: t('auth.login.messages.validation'),
        severity: "warning",
        title: "Validation Error",
      });
    }
  };

  return (
    <AuthWrapper dir={isRtl ? 'rtl' : 'ltr'}>
      <BackToHomeLink to="/">
        <ArrowIcon>←</ArrowIcon>
        {t('auth.login.backToHome')}
      </BackToHomeLink>

      <AuthCard>
        <ContentBox>
          <LogoContainer>
            <img src={Logo} alt="Logo" />
          </LogoContainer>

          <Typography variant="h5" align="center" gutterBottom fontWeight={600}>
            {t('auth.login.title')}
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="textSecondary"
            sx={{ mb: 3 }}
          >
            {t('auth.login.subtitle')}
          </Typography>

          {message && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <StyledTextField
              autoFocus
              fullWidth
              placeholder={t('auth.login.fields.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <StyledTextField
              fullWidth
              placeholder={t('auth.login.fields.password')}
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                mb: 1,
              }}
            >
              <Link
                to="/forgot-password"
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  fontSize: "0.875rem",
                }}
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 2,
                height: 48,
                borderRadius: 2,
                position: "relative",
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              ) : (
                t('auth.login.buttons.login')
              )}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {t('auth.login.signup.text')}{" "}
              <MuiLink
                component={Link}
                to="/register"
                sx={{ color: "primary.main", fontWeight: 500 }}
              >
                {t('auth.login.signup.link')}
              </MuiLink>
            </Typography>
          </Box>
        </ContentBox>
      </AuthCard>

      <SnackbarMessage
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
        title={snackbar.title}
        position={{ vertical: "top", horizontal: "right" }}
      />
    </AuthWrapper>
  );
};

export default Login;
